/* eslint-disable no-console */
import { apiClient, apiEndpoint } from './api.services';
import { API } from '../constants/api.constants';

/**
 * Fetch purchase order todos list with pagination and filtering
 * @param {Object} params - Query parameters
 * @param {number} [params.pageIndex] - Page number (1-based)
 * @param {number} [params.pageSize] - Number of items per page
 * @param {string} [params.searchText] - Search text for filtering
 * @param {Object} [params.filters] - Filter parameters
 * @param {string[]} [params.filters.supplierIds] - Array of supplier IDs
 * @param {string[]} [params.filters.colourwayIds] - Array of colourway IDs
 * @param {string[]} [params.filters.statuses] - Array of status values
 * @returns {Promise<any>}
 */
export const fetchPurchaseOrderTodosList = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();

    // Add pagination parameters
    if (params?.pageIndex) queryParams.append('pageIndex', params.pageIndex);
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize);
    if (params?.searchText?.trim()) queryParams.append('searchText', params.searchText.trim());

    // Add filter parameters
    const filters = params?.filters;
    if (filters) {
      if (Array.isArray(filters.supplierIds) && filters.supplierIds.length > 0) {
        queryParams.append('supplierIds', filters.supplierIds.join(','));
      }
      if (Array.isArray(filters.colourwayIds) && filters.colourwayIds.length > 0) {
        queryParams.append('colourwayIds', filters.colourwayIds.join(','));
      }
      if (Array.isArray(filters.statuses) && filters.statuses.length > 0) {
        queryParams.append('statuses', filters.statuses.join(','));
      }
    }

    const fullUrl = `${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER_TODO.FETCH_LIST}?${queryParams.toString()}`;
    return await apiClient.get(fullUrl);
  } catch (error) {
    console.error('Error in fetchPurchaseOrderTodosList:', error);
    throw error;
  }
};

/**
 * Update actual fields for a purchase order todo
 * @param {string} orderId - The ID of the purchase order todo
 * @param {Object} updates - The fields to update
 * @returns {Promise<any>}
 */
export const updateActualFields = async (orderId, updates) => {
  try {
    if (!orderId) {
      throw new Error('Order ID is required');
    }
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const payload = {
      ...updates,
      timezone,
    };
    return await apiClient.put(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER_TODO.UPDATE_ACTUAL_FIELDS}/${orderId}`, payload);
  } catch (error) {
    console.error('Error in updateActualFields:', error);
    throw error;
  }
};

/**
 * Delete a single purchase order todo
 * @param {string} todoId - The ID of the todo to delete
 * @returns {Promise<any>}
 */
export const deletePurchaseOrderTodo = async (todoId) => {
  try {
    if (!todoId) {
      throw new Error('Todo ID is required');
    }

    return await apiClient.delete(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER_TODO.DELETE}/${todoId}`);
  } catch (error) {
    console.error('Error in deletePurchaseOrderTodo:', error);
    throw error;
  }
};

/**
 * Bulk delete multiple purchase order todos
 * @param {string[]} todoIds - Array of todo IDs to delete
 * @returns {Promise<any>}
 */
export const bulkDeletePurchaseOrderTodos = async (todoIds) => {
  try {
    if (!Array.isArray(todoIds) || todoIds.length === 0) {
      throw new Error('Todo IDs array is required and must not be empty');
    }

    return await apiClient.post(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER_TODO.BULK_DELETE}`, { todoIds });
  } catch (error) {
    console.error('Error in bulkDeletePurchaseOrderTodos:', error);
    throw error;
  }
};

/**
 * Get purchase order todo count for TODO tab with filtering
 * @param {Object} [params={}] - Query parameters
 * @param {string} [params.searchText] - Search text for filtering
 * @param {Object} [params.filters] - Filter parameters
 * @param {string[]} [params.filters.supplierIds] - Array of supplier IDs
 * @param {string[]} [params.filters.colourwayIds] - Array of colourway IDs
 * @param {string[]} [params.filters.statuses] - Array of status values
 * @returns {Promise<any>}
 */
export const getPurchaseOrderTodoCount = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();

    // Add search text parameter
    if (params.searchText?.trim()) {
      queryParams.append('searchText', params.searchText.trim());
    }

    // Add filter parameters
    if (params.filters) {
      if (Array.isArray(params.filters.supplierIds) && params.filters.supplierIds.length > 0) {
        queryParams.append('supplierIds', params.filters.supplierIds.join(','));
      }
      if (Array.isArray(params.filters.colourwayIds) && params.filters.colourwayIds.length > 0) {
        queryParams.append('colourwayIds', params.filters.colourwayIds.join(','));
      }
      if (Array.isArray(params.filters.statuses) && params.filters.statuses.length > 0) {
        queryParams.append('statuses', params.filters.statuses.join(','));
      }
    }

    return await apiClient.get(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER_TODO.COUNT}?${queryParams.toString()}`);
  } catch (error) {
    console.error('Error in getPurchaseOrderTodoCount:', error);
    throw error;
  }
};

/**
 * Export selected purchase order todos to Excel
 * @param {string[]} selectedIds - Array of selected purchase order todo IDs
 * @returns {Promise<Object>} Response envelope containing the presigned S3 download URL
 */
export const exportSelectedPurchaseOrderTodos = async (selectedIds) => {
  try {
    if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
      throw new Error('Selected IDs array is required and must not be empty');
    }

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return await apiClient.post(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER_TODO.EXPORT_SELECTED}`, { selectedIds, timezone }, { responseType: 'blob' });
  } catch (error) {
    console.error('Error in exportSelectedPurchaseOrderTodos:', error);
    throw error;
  }
};

/**
 * Export all purchase order todos to Excel with optional filters
 * @param {Object} [params={}] - Query parameters for filtering
 * @param {string} [params.searchText] - Search text for filtering
 * @param {Object} [params.filters] - Filter parameters
 * @param {string[]} [params.filters.supplierIds] - Array of supplier IDs
 * @param {string[]} [params.filters.statuses] - Array of status values
 * @returns {Promise<Object>} Response envelope containing the presigned S3 download URL
 */
export const exportAllPurchaseOrderTodos = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();

    // Add search text parameter
    if (params?.searchText?.trim()) {
      queryParams.append('searchText', params.searchText.trim());
    }

    // Add filter parameters
    const filters = params?.filters;
    if (filters) {
      if (Array.isArray(filters.supplierIds) && filters.supplierIds.length > 0) {
        queryParams.append('supplierIds', filters.supplierIds.join(','));
      }
      if (Array.isArray(filters.statuses) && filters.statuses.length > 0) {
        queryParams.append('statuses', filters.statuses.join(','));
      }
    }

    // Add timezone parameter for correct date conversion
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    queryParams.append('timezone', timezone);

    const fullUrl = `${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER_TODO.EXPORT_ALL}?${queryParams.toString()}`;
    return await apiClient.get(fullUrl, { responseType: 'blob' });
  } catch (error) {
    console.error('Error in exportAllPurchaseOrderTodos:', error);
    throw error;
  }
};

/**
 * Get presigned URL for uploading PO Excel file to S3
 * @param {Object} params - Parameters for presigned URL generation
 * @param {string} params.fileName - Name of the file to upload
 * @returns {Promise<Object>} Response containing presigned URL
 */
export const getPresignedUrlForPOUpload = async (fileName) => {
  try {
    if (!fileName) {
      throw new Error('File name is required');
    }

    return await apiClient.post(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER_TODO.PRESIGNED_UPLOAD}`, {
      fileName,
    });
  } catch (error) {
    console.error('Error in getPresignedUrlForPOUpload:', error);
    throw error;
  }
};

/**
 * Move error sheet from Lambda_PO_Error/ to Lambda_PO/confirmed/ to trigger update
 * @param {Object} params - Parameters for moving error sheet
 * @param {string} params.errorSheetKey - S3 key of the error sheet to move
 * @param {string} [params.originalKey] - Original S3 key before error sheet was created
 * @returns {Promise<Object>} Response containing move operation result
 */
export const moveErrorSheetToUpdate = async ({ errorSheetKey, originalKey }) => {
  try {
    return await apiClient.post(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER_TODO.MOVE_ERROR_SHEET_TO_UPDATE}`, {
      errorSheetKey,
      originalKey,
    });
  } catch (error) {
    console.error('Error in moveErrorSheetToUpdate:', error);
    throw error;
  }
};
